/**
 * Scroll a report-page element into view inside the scaled media-container scroller.
 *
 * Report pages use `.performance-charts-scroll-container` (or similar) under a transform/zoom
 * scaler, so `Element.scrollIntoView` is unreliable — see chartSearchTargets.scrollToChartSearchTarget.
 * Also retries: Show-me often fires while a loading gate has unmounted the target.
 */

import { computeChartScrollTop, findScrollParent } from './chartSearchTargets';
import { getEffectiveScale } from './effectiveScale';
import { debug as logDebug } from './console';

function scrollOnce(el: HTMLElement, marginPx: number): void {
  const sc = findScrollParent(el);
  sc.scrollTop = computeChartScrollTop({
    currentScrollTop: sc.scrollTop,
    targetPaintedTop: el.getBoundingClientRect().top,
    scrollerPaintedTop: sc.getBoundingClientRect().top,
    scrollerClientHeight: sc.clientHeight,
    scrollerScrollHeight: sc.scrollHeight,
    scale: getEffectiveScale(sc),
    marginPx,
  });
}

/**
 * Scroll the first matching connected element into its report scroller.
 * Retries across a few frames/timeouts so a remount after loading still lands.
 */
export function scrollToEvidence(
  selector: string,
  options?: { marginPx?: number; root?: ParentNode }
): void {
  const marginPx = options?.marginPx ?? 12;
  const root = options?.root ?? document;
  const delaysMs = [0, 50, 150, 350, 700];

  const tryScroll = (attempt: number): void => {
    const el = root.querySelector(selector);
    if (el instanceof HTMLElement && el.isConnected) {
      scrollOnce(el, marginPx);
      // Second pass after layout settles (map/table height changes).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (el.isConnected) {
            scrollOnce(el, marginPx);
          }
        });
      });
      logDebug('scrollToEvidence: scrolled', { selector, attempt });
      return;
    }
    const next = delaysMs[attempt + 1];
    if (next == null) {
      logDebug('scrollToEvidence: target not found', { selector });
      return;
    }
    window.setTimeout(() => tryScroll(attempt + 1), next - (delaysMs[attempt] ?? 0));
  };

  requestAnimationFrame(() => tryScroll(0));
}
